import React from "react";
import {
  Box,
  Card,
  Typography,
  IconButton,
  LinearProgress,
  Divider,
  Tooltip,
  Chip
} from "@mui/material";
import { BsPencilSquare, BsTrashFill, BsCurrencyDollar } from "react-icons/bs";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";

export default function PackageCard({
  data, onClick = () => { }, keys, onAction = () => { },
  canEdit, canDelete, editPermission, deletePermission
}) {
  const authUser = useSelector((state) => state?.loginState?.auth);
  const isSuperUser = Boolean(authUser?.is_superuser || authUser?.main_role === "SuperAdmin");
  const permissions = authUser?.permissions || [];

  const hasPerm = (permName) => {
    if (!permName) return true;
    if (isSuperUser) return true;
    return Array.isArray(permissions) && permissions.includes(permName);
  };

  let showEdit = true;
  if (typeof canEdit === "boolean") {
    showEdit = canEdit;
  } else if (editPermission) {
    showEdit = hasPerm(editPermission);
  } else {
    showEdit = hasPerm("package.change_package");
  }

  let showDelete = true;
  if (typeof canDelete === "boolean") {
    showDelete = canDelete;
  } else if (deletePermission) {
    showDelete = hasPerm(deletePermission);
  } else {
    showDelete = hasPerm("package.delete_package");
  }

  let progress = 0;
  if (data?.title === "Basic") progress = 25;
  if (data?.title === "Standared") progress = 50;
  if (data?.title === "Premium") progress = 100;

  const isExpired = Boolean(
    data?.validity_date && new Date(data.validity_date + "T23:59:59") < new Date()
  );

  return (
    <Card
      sx={{
        p: 3,
        cursor: "pointer",
        // background: 'linear-gradient(90deg, rgba(27, 112, 24, 1) 0%, rgb(80 147 108) 50%, rgb(59 107 67) 100%)',
        transition: "0.3s",
        "&:hover": {
          boxShadow: 6,
          transform: "translateY(-4px)",
        },
      }}
      onClick={onClick}
    >
      <Box textAlign="center" mb={2}>
        <Typography
          variant="h5"
          fontWeight="bold"
          gutterBottom
          sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.8 }}
        >
          <span style={{ fontSize: "0.95rem", color: "#64748b", fontWeight: 700 }}>
            {data?.currency || "USD"}
          </span>
          <span>{Number(data[keys[0]] || 0).toLocaleString()}</span>
        </Typography>
        <Typography variant="subtitle1">{data[keys[1]]}</Typography>
        {data?.validity_date && (
          <Chip
            size="small"
            label={isExpired ? "Validity Expired" : `Valid till: ${data.validity_date}`}
            color={isExpired ? "error" : "success"}
            sx={{ mt: 1, fontSize: "0.75rem", height: 22 }}
          />
        )}
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Box
        display="grid"
        gridTemplateColumns="1fr 1fr"
        gap={1}
        mb={2}
        sx={{ fontSize: "0.9rem" }}
      >
        <Info label="Days" value={`${data[keys[2]]} Days`} />
        <Info
          label="Validity Date"
          value={
            data?.validity_date ? (
              <span style={{ color: isExpired ? "#d32f2f" : "#2e7d32", fontWeight: 600 }}>
                {data.validity_date}
              </span>
            ) : "No Expiry"
          }
        />
        <Info label="Image Retention" value={`${data[keys[3]]} Days`} />
        <Info label="Video Retention" value={`${data[keys[4]]} Days`} />
        <Info label="AI Cameras" value={`${data[keys[5]]} Nos`} />
        <Info label="HD PTZ Cameras" value={`${data[keys[6]]} Nos`} />
        <Info label="4K PTZ Cameras" value={`${data[keys[7]]} Nos`} />
        <Info label="24MP Cameras" value={`${data[keys[8]]} Nos`} />
        <Info label="32MP Cameras" value={`${data[keys[9]]} Nos`} />
        <Info label="50MP Cameras" value={`${data[keys[10]]} Nos`} />
        <Info label="60MP Cameras" value={`${data[keys[11]]} Nos`} />
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box mb={2}>
        <Typography variant="subtitle2" fontWeight="medium">
          Description
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {data[keys[12]]}
        </Typography>
      </Box>

      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="caption" color="text.secondary">
            Created At:
          </Typography>
          <Typography variant="caption">{data.created_at}</Typography>
        </Box>

        <Box display="flex" gap={1}>
          {showEdit && (
            <Tooltip title="Edit">
              <IconButton
                size="small"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  onAction("Edit", data);
                }}
              >
                <BsPencilSquare />
              </IconButton>
            </Tooltip>
          )}
          {showDelete && (
            <Tooltip title="Delete">
              <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  Swal.fire({
                    title: "Are you sure?",
                    text: "You won't be able to revert this!",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#3085d6",
                    cancelButtonColor: "#d33",
                    confirmButtonText: "Yes, delete it!",
                  }).then((result) => {
                    if (result.isConfirmed) {
                      onAction("Delete", data);
                    }
                  });
                }}
              >
                <BsTrashFill />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Box mt={2}>
        <LinearProgress
          color="success"
          variant="determinate"
          value={progress}
          sx={{ borderRadius: 2 }}
        />
      </Box>
    </Card>
  );
}

function Info({ label, value }) {
  return (
    <Box>
      <Typography variant="body2" fontWeight="bold">
        {label}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {value}
      </Typography>
    </Box>
  );
}
